import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Modal, Spinner, Table } from "react-bootstrap";
import { AlertTriangle } from "react-feather";
import { api, apiErrorMessage, useGet, usePost } from "../../api";
import {
  COUPON_ISSUE_LABELS,
  CouponIssueType,
  ICouponIssueFeedSummary,
} from "../../interfaces";

type TRun = ICouponIssueFeedSummary & { _id: string; trigger: string };

const Summary: React.FC<{ s: ICouponIssueFeedSummary }> = ({ s }) => (
  <>
    {s.error && (
      <Alert variant="danger" className="py-2 small">
        {s.error}
      </Alert>
    )}
    <Table size="sm" className="small mb-2">
      <tbody>
        {Object.values(CouponIssueType).map((t) => (
          <tr key={t}>
            <td>{COUPON_ISSUE_LABELS[t]}</td>
            <td className="text-end">{s.found[t] ?? 0}</td>
          </tr>
        ))}
        <tr className="table-light">
          <td>New issues</td>
          <td className="text-end">{s.created}</td>
        </tr>
        <tr>
          <td>Reopened (came back after a fix)</td>
          <td className="text-end">{s.reopened}</td>
        </tr>
        <tr>
          <td>Already open</td>
          <td className="text-end">{s.alreadyOpen}</td>
        </tr>
        <tr>
          <td>Marked "Not an issue" earlier — left alone</td>
          <td className="text-end">{s.dismissed}</td>
        </tr>
        <tr>
          <td>Closed because the code changed</td>
          <td className="text-end">{s.resolved}</td>
        </tr>
        <tr className="table-light">
          <td>Domains already on the board</td>
          <td className="text-end">{s.boardRowsExisting}</td>
        </tr>
        <tr>
          <td>Domains added to "Auto-flagged coupons"</td>
          <td className="text-end">{s.boardRowsAdded}</td>
        </tr>
        <tr className="text-warning">
          <td title="Live codes whose coupon, deal and brand don't name exactly one site. Filter Coupon Performance by 'no domain' to fix them.">
            Codes skipped — no domain
          </td>
          <td className="text-end">{s.noDomain}</td>
        </tr>
        <tr className="text-warning">
          <td title="Codes with no shopper country in analytics and no enabled market on the coupon.">
            Codes skipped — no country
          </td>
          <td className="text-end">{s.noGeo}</td>
        </tr>
      </tbody>
    </Table>
    <small className="text-muted">
      Window {new Date(s.window.from).toLocaleString()} –{" "}
      {new Date(s.window.to).toLocaleString()}
    </small>
  </>
);

/**
 * Manager control for the daily coupon-issue feed: dry run, read the counts,
 * then apply. The same run happens on its own at 07:00 UTC once switched on.
 */
export const CouponIssuesRunButton: React.FC<{ onApplied: () => void }> = ({
  onApplied,
}) => {
  const [show, setShow] = useState(false);
  const [dry, setDry] = useState<ICouponIssueFeedSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const runReq = usePost<ICouponIssueFeedSummary | { started: boolean }>();
  const { data: runs, refetch } = useGet<{ running: boolean; results: TRun[] }>(
    show ? "work-pipeline/coupon-issues/runs" : null
  );
  const poll = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(
    () => () => {
      if (poll.current) clearInterval(poll.current);
    },
    []
  );

  const dryRun = async () => {
    setError(null);
    setDry(null);
    try {
      setDry(
        (await runReq.execute("work-pipeline/coupon-issues/run", {
          dryRun: true,
        })) as ICouponIssueFeedSummary
      );
    } catch (err) {
      setError(apiErrorMessage(err, "Dry run failed."));
    }
  };

  const apply = async () => {
    setError(null);
    setApplying(true);
    try {
      await runReq.execute("work-pipeline/coupon-issues/run", {
        dryRun: false,
      });
      poll.current = setInterval(async () => {
        const status = await api
          .get<{ running: boolean }>("work-pipeline/coupon-issues/runs")
          .catch(() => null);
        if (status && !status.running) {
          if (poll.current) clearInterval(poll.current);
          setApplying(false);
          setDry(null);
          await refetch();
          onApplied();
        }
      }, 5000);
    } catch (err) {
      setApplying(false);
      setError(apiErrorMessage(err, "Couldn't start the run."));
    }
  };

  const last = runs?.results?.find((r) => !r.dryRun);

  return (
    <>
      <Button
        variant="outline-secondary"
        size="sm"
        onClick={() => setShow(true)}
        title="Raise coupons to suspend or re-enable, and broken templates, onto the board"
      >
        <AlertTriangle size={14} className="me-1" /> Coupon issues
      </Button>
      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title as="h6">Coupon issues → Work Pipeline</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted">
            Looks at the last 24 hours: Coupon Results "Needs Suspension" and
            "Needs Activation", live codes that never applied for shoppers, and
            "Coupon box not found". Do a dry run first — it changes nothing.
          </p>
          {error && (
            <Alert variant="danger" className="py-2 small">
              {error}
            </Alert>
          )}
          {dry ? (
            <>
              <div className="fw-semibold small mb-1">Dry run</div>
              <Summary s={dry} />
            </>
          ) : last ? (
            <>
              <div className="fw-semibold small mb-1">
                Last applied run ({last.trigger})
              </div>
              <Summary s={last} />
            </>
          ) : (
            <small className="text-muted">No run applied yet.</small>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={runReq.loading || applying}
            onClick={dryRun}
            title="Work out what would be raised, without writing anything"
          >
            {runReq.loading && !applying && (
              <Spinner animation="border" size="sm" className="me-1" />
            )}
            Dry run
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={!dry || !!dry.error || runReq.loading || applying}
            onClick={apply}
            title="Raise the issues and add domains to the board. Runs in the background."
          >
            {applying && <Spinner animation="border" size="sm" className="me-1" />}
            {applying ? "Applying…" : "Apply"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
